import { api } from '../lib/api';
import { useFetch } from '../lib/useFetch';
import { fmtMoney, fmtMoneyShort } from '../lib/format';
import { HOJE } from '../lib/ui';
import type { PageProps } from '../App';

const MESES = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];

export function Dashboard({ navigate }: PageProps) {
  const kpis = useFetch(() => api.kpis(), []);
  const pag = useFetch(() => api.pagtos30d(), []);
  const fila = useFetch(() =>
    Promise.all([
      api.listContas({ classificacao_status: 'sugerida', limit: 1 }).then((r) => r.total).catch(() => 0),
      api.listComprovantes({ match_status: 'pending', limit: 1 }).then((r) => r.total).catch(() => 0),
    ]).then(([a, b]) => ({ sugeridas: a, difs: b })), []);

  const k = kpis.data;
  const now = new Date();
  const mesAno = `${MESES[now.getMonth()]}/${now.getFullYear()}`;

  // Gráfico 30d: agrega os pagamentos diários em 4 semanas
  const buckets = [0, 0, 0, 0];
  (pag.data?.items ?? []).forEach((p) => {
    const dias = Math.max(0, Math.floor((new Date(p.date).getTime() - new Date(HOJE).getTime()) / 86400000));
    const wk = Math.min(3, Math.floor(dias / 7));
    buckets[wk] += p.valor;
  });
  const maxBucket = Math.max(1, ...buckets);
  const chartLabels = ['Semana 1', 'Semana 2', 'Semana 3', 'Semana 4'];

  const filaTotal = (fila.data?.sugeridas ?? 0) + (fila.data?.difs ?? 0);

  return (
    <div className="page wide">
      <h1>Dashboard</h1>
      <div className="subtitle">Visão geral · {mesAno}</div>

      {kpis.loading ? (
        <div className="loading">Carregando indicadores…</div>
      ) : (
        <div className="kpis">
          <div className="kpi" onClick={() => navigate('contas', { status: 'a_vencer' })}>
            <div className="k-label">A pagar no mês</div>
            <div className="k-val">{fmtMoney(k?.a_pagar_mes.total ?? 0)}</div>
            <div className="k-sub">{k?.a_pagar_mes.cnt ?? 0} contas</div>
          </div>
          <div className="kpi" onClick={() => navigate('contas', { status: 'atrasadas' })}>
            <div className="k-label">Atrasadas</div>
            <div className={`k-val ${(k?.atrasadas.cnt ?? 0) > 0 ? 'danger' : ''}`}>{fmtMoney(k?.atrasadas.total ?? 0)}</div>
            <div className="k-sub">{k?.atrasadas.cnt ?? 0} contas vencidas</div>
          </div>
          <div className="kpi" onClick={() => navigate('contas', { status: 'pago' })}>
            <div className="k-label">Pago no mês</div>
            <div className="k-val">{fmtMoney(k?.pagas_mes.total ?? 0)}</div>
            <div className="k-sub">{k?.pagas_mes.cnt ?? 0} pagamentos</div>
          </div>
          <div className="kpi" onClick={() => navigate('fila')}>
            <div className="k-label">Comprovantes órfãos</div>
            <div className="k-val">{k?.comp_orfaos.cnt ?? 0}</div>
            <div className="k-sub">{fmtMoney(k?.comp_orfaos.total ?? 0)} sem conta</div>
          </div>
          <div className="kpi" onClick={() => navigate('contas', { sem_nf: true })}>
            <div className="k-label">Pagas sem NF</div>
            <div className="k-val" style={(k?.pagas_sem_nf?.cnt ?? 0) > 0 ? { color: 'var(--amber, #b45309)' } : undefined}>{k?.pagas_sem_nf?.cnt ?? 0}</div>
            <div className="k-sub">{fmtMoney(k?.pagas_sem_nf?.total ?? 0)} aguardando nota</div>
          </div>
        </div>
      )}

      <div className="grid-2" style={{ gridTemplateColumns: '1.4fr 1fr' }}>
        <div className="card pad">
          <div className="card-title">Próximos vencimentos</div>
          {(k?.proximas_7d.length ?? 0) === 0 ? (
            <div className="muted text-sm" style={{ padding: '8px 0' }}>Nenhum vencimento nos próximos 7 dias.</div>
          ) : (
            (k?.proximas_7d ?? []).map((r) => (
              <div key={r.id} className="tbl-row click"
                style={{ display: 'flex', padding: '9px 0', gap: 12 }}
                onClick={() => navigate('detalhe', { id: r.id })}>
                <span className="mono text-sm muted" style={{ width: 52 }}>{fmtVencShort(r.vencimento)}</span>
                <span style={{ flex: 1, fontWeight: 500 }} className="ellipsis">{r.fornecedor}</span>
                <span className="mono cell-strong">{fmtMoney(r.valor)}</span>
              </div>
            ))
          )}
        </div>

        <div className="stack">
          <div className="fila-card" onClick={() => navigate('fila')}>
            <div className="fc-label">A confirmar</div>
            <div className="fc-num">{fila.loading ? '…' : filaTotal}</div>
            <div className="fc-sub">{fila.data?.sugeridas ?? 0} classificações · {fila.data?.difs ?? 0} diferenças de pagamento</div>
            <div className="fc-cta">Abrir fila →</div>
          </div>
          <div className="card pad" style={{ flex: 1 }}>
            <div className="card-title">Pagamentos — próximos 30 dias</div>
            {pag.loading ? (
              <div className="muted text-sm">Carregando…</div>
            ) : (
              buckets.map((v, i) => (
                <div className="bar-row" key={i}>
                  <span className="b-label">{chartLabels[i]}</span>
                  <div className="bar-track"><div className="bar-fill" style={{ width: `${(v / maxBucket) * 100}%` }} /></div>
                  <span className="b-val">{v > 0 ? fmtMoneyShort(v) : '—'}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function fmtVencShort(d: string | null): string {
  if (!d) return '—';
  const m = d.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}` : d;
}
